/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `weather_conditions` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class WeatherConditionsMesg : Mesg {
    public constructor() : super("WeatherConditions", Profile.MesgNum.WEATHER_CONDITIONS)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. time of update for current conditions, else forecast time */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** weather_report. Current or forecast */
    public var weatherReport: WeatherReport?
        get() = (getFieldValue(WEATHER_REPORT_FIELD_NUM) as? UByte)?.let { WeatherReport.fromValue(it) }
        set(value) = setFieldValue(WEATHER_REPORT_FIELD_NUM, value?.value)

    /** temperature, in C. */
    public var temperature: Byte?
        get() = getFieldValue(TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(TEMPERATURE_FIELD_NUM, value)

    /** condition. Corresponds to GSC Response weatherIcon field */
    public var condition: WeatherStatus?
        get() = (getFieldValue(CONDITION_FIELD_NUM) as? UByte)?.let { WeatherStatus.fromValue(it) }
        set(value) = setFieldValue(CONDITION_FIELD_NUM, value?.value)

    /** wind_direction, in degrees. */
    public var windDirection: UShort?
        get() = getFieldValue(WIND_DIRECTION_FIELD_NUM) as? UShort
        set(value) = setFieldValue(WIND_DIRECTION_FIELD_NUM, value)

    /** wind_speed, in m/s. */
    public var windSpeed: Double?
        get() = getFieldValue(WIND_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(WIND_SPEED_FIELD_NUM, value)

    /** precipitation_probability. range 0-100 */
    public var precipitationProbability: UByte?
        get() = getFieldValue(PRECIPITATION_PROBABILITY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(PRECIPITATION_PROBABILITY_FIELD_NUM, value)

    /** temperature_feels_like, in C. Heat Index if GCS heatIdx above or equal to 90F or wind chill if GCS windChill below or equal to 32F */
    public var temperatureFeelsLike: Byte?
        get() = getFieldValue(TEMPERATURE_FEELS_LIKE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(TEMPERATURE_FEELS_LIKE_FIELD_NUM, value)

    /** relative_humidity. */
    public var relativeHumidity: UByte?
        get() = getFieldValue(RELATIVE_HUMIDITY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(RELATIVE_HUMIDITY_FIELD_NUM, value)

    /** location. string corresponding to GCS response location string */
    public var location: String?
        get() = getFieldValue(LOCATION_FIELD_NUM) as? String
        set(value) = setFieldValue(LOCATION_FIELD_NUM, value)

    /** observed_at_time. */
    public var observedAtTime: Instant?
        get() = (getFieldValue(OBSERVED_AT_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(OBSERVED_AT_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** observed_location_lat, in semicircles. */
    public var observedLocationLat: Int?
        get() = getFieldValue(OBSERVED_LOCATION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(OBSERVED_LOCATION_LAT_FIELD_NUM, value)

    /** observed_location_long, in semicircles. */
    public var observedLocationLong: Int?
        get() = getFieldValue(OBSERVED_LOCATION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(OBSERVED_LOCATION_LONG_FIELD_NUM, value)

    /** day_of_week. */
    public var dayOfWeek: DayOfWeek?
        get() = (getFieldValue(DAY_OF_WEEK_FIELD_NUM) as? UByte)?.let { DayOfWeek.fromValue(it) }
        set(value) = setFieldValue(DAY_OF_WEEK_FIELD_NUM, value?.value)

    /** high_temperature, in C. */
    public var highTemperature: Byte?
        get() = getFieldValue(HIGH_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(HIGH_TEMPERATURE_FIELD_NUM, value)

    /** low_temperature, in C. */
    public var lowTemperature: Byte?
        get() = getFieldValue(LOW_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(LOW_TEMPERATURE_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val WEATHER_REPORT_FIELD_NUM: UByte = 0u
        public const val TEMPERATURE_FIELD_NUM: UByte = 1u
        public const val CONDITION_FIELD_NUM: UByte = 2u
        public const val WIND_DIRECTION_FIELD_NUM: UByte = 3u
        public const val WIND_SPEED_FIELD_NUM: UByte = 4u
        public const val PRECIPITATION_PROBABILITY_FIELD_NUM: UByte = 5u
        public const val TEMPERATURE_FEELS_LIKE_FIELD_NUM: UByte = 6u
        public const val RELATIVE_HUMIDITY_FIELD_NUM: UByte = 7u
        public const val LOCATION_FIELD_NUM: UByte = 8u
        public const val OBSERVED_AT_TIME_FIELD_NUM: UByte = 9u
        public const val OBSERVED_LOCATION_LAT_FIELD_NUM: UByte = 10u
        public const val OBSERVED_LOCATION_LONG_FIELD_NUM: UByte = 11u
        public const val DAY_OF_WEEK_FIELD_NUM: UByte = 12u
        public const val HIGH_TEMPERATURE_FIELD_NUM: UByte = 13u
        public const val LOW_TEMPERATURE_FIELD_NUM: UByte = 14u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("WeatherReport", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("Temperature", 1u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[2] = Field("Condition", 2u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("WindDirection", 3u, BaseType.UINT16, 1.0, 0.0, "degrees", false)
            fields[4] = Field("WindSpeed", 4u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[5] = Field("PrecipitationProbability", 5u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[6] = Field("TemperatureFeelsLike", 6u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[7] = Field("RelativeHumidity", 7u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[8] = Field("Location", 8u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[9] = Field("ObservedAtTime", 9u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[10] = Field("ObservedLocationLat", 10u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[11] = Field("ObservedLocationLong", 11u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[12] = Field("DayOfWeek", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[13] = Field("HighTemperature", 13u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[14] = Field("LowTemperature", 14u, BaseType.SINT8, 1.0, 0.0, "C", false)
            return fields
        }
    }
}
